import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  Hidden,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import CustomDatePicker from "../../../../../components/input/CustomDatePicker";
import { useTranslation } from "react-i18next";
import {
  AddEmissionContext,
  EmissionMode,
} from "../../../../../contexts/AddEmissionContext";
import { Controller, useFormContext } from "react-hook-form";
import CustomInput from "../../../../../components/input/CustomInput";
import { useEffect, useState, useRef } from "react";
import { EmissionFactorDataV2 } from "../../../../../models/EmissionFactors";
import { CustomAutoComplete } from "../../../../../components/input/CustomAutoComplete";
import ArrayUtils from "../../../../../utils/arrayUtils";
import { DROPDOWN_EMISSION_GROUP } from "../../../../../components/emission/emission-components";
import { EmissionGroup } from "../../../../../models/EmissionGroup";
import { common, input } from "../../../../../i18n";
import { InputAmountTypeRadio } from "../../InputAmountTypeRadio";
import { cat3UpstreamPublisher } from "../../../../../utils/emissionUtils";

const AddEmissionSectionOne = () => {
  const { t } = useTranslation([common, input], {
    nsMode: "fallback",
  });

  const {
    stepOneForm,
    emissionFactorsMasterData,
    assetList,
    stepTwoForm,
    emissionMode,
    disableMonthly,
    refreshEmissionFactors, // ✅ ดึงฟังก์ชันมาจาก Context
  } = AddEmissionContext();

  const { setValue, control, watch, getValues, clearErrors } = useFormContext();

  const [mode, setMode] = useState<any | null>(null);

  const clearedFields = useRef<Set<string>>(new Set());

  const startDate = watch("startDate");
  const emissionGroup = stepOneForm.watch("emissionGroup");
  const activityGroup = stepTwoForm.watch("activityGroup");
  const activityType = stepTwoForm.watch("activityType");
  const activityAttribute = stepTwoForm.watch("activityAttribute");
  const unit = stepTwoForm.watch("unit");
  const selectedPublisher = stepTwoForm.watch(
    "activityEmissionFactorPublisher",
  );

  const [activityAttributeHl, setActivityAttributeHl] = useState("");

  const buildActivityGroupLabel = () => {
    if (emissionGroup) {
      const currentGroup: string = stepOneForm.getValues("emissionGroup") ?? "";

      const item: EmissionGroup | undefined = DROPDOWN_EMISSION_GROUP.find(
        (e) => e.emissionGroup === currentGroup,
      );

      setActivityAttributeHl(t(item?.attributeName ?? ""));
    }
  };

  const changeValue = async (event: any, fieldName: string) => {
    setMode(event);
    const isCleared = event.target.value === null;

    if (isCleared) {
      clearedFields.current.add(fieldName);
    } else {
      clearedFields.current.delete(fieldName);
    }

    switch (fieldName) {
      case "activityType":
        if (!isCleared) {
          clearedFields.current.delete("activityAttribute");
          clearedFields.current.delete("unit");
          clearedFields.current.delete("activityEmissionFactorPublisher");
        }
        setValue("activityAttribute", null);
        setValue("unit", null);
        setValue("activityEmissionFactorPublisher", null);
        break;
      case "activityAttribute":
        if (!isCleared) {
          clearedFields.current.delete("unit");
          clearedFields.current.delete("activityEmissionFactorPublisher");
        }
        setValue("unit", null);
        setValue("activityEmissionFactorPublisher", null);
        break;
      case "unit":
        if (!isCleared) {
          clearedFields.current.delete("activityEmissionFactorPublisher");
        }
        setValue("activityEmissionFactorPublisher", null);
        break;
      default:
        break;
    }
  };

  const setSingleMenuValue = (
    menu: { value: string }[],
    form: any,
    field: string,
  ) => {
    if (
      menu.length === 1 &&
      !form.getValues(field) &&
      !clearedFields.current.has(field)
    ) {
      form.setValue(field, menu[0].value, {
        shouldValidate: true,
      });
    }
  };

  const buildSelect = async () => {
    const activityGroupMenu = filterActivityGroup();
    const activityTypeMenu = filterActivityType();
    const activityAttributeMenu = filterActivityAttribute();
    const unitMenu = filterActivityUnit();
    const publisherMenu = filterPublishers();

    autoSelect(
      activityGroupMenu,
      activityTypeMenu,
      activityAttributeMenu,
      unitMenu,
      publisherMenu,
    );
  };

  const autoSelect = (
    activityGroupMenu: any,
    activityTypeMenu: any,
    activityAttributeMenu: any,
    unitMenu: any,
    publisherMenu: any,
  ) => {
    setSingleMenuValue(activityGroupMenu, stepTwoForm, "activityGroup");
    setSingleMenuValue(activityAttributeMenu, stepTwoForm, "activityAttribute");
    setSingleMenuValue(activityTypeMenu, stepTwoForm, "activityType");
    setSingleMenuValue(unitMenu, stepTwoForm, "unit");

    if (
      publisherMenu.length > 0 &&
      !stepTwoForm.getValues("activityEmissionFactorPublisher") &&
      !clearedFields.current.has("activityEmissionFactorPublisher")
    ) {
      stepTwoForm.setValue(
        "activityEmissionFactorPublisher",
        publisherMenu[0].value,
        { shouldValidate: true },
      );
    }
  };

  const filterActivityGroup = () => {
    const activityType = stepTwoForm.getValues("activityType") ?? "";
    const activityAttribute = stepTwoForm.getValues("activityAttribute") ?? "";
    const unit = stepTwoForm.getValues("unit") ?? "";
    let filterResult: EmissionFactorDataV2[] = [...emissionFactorsMasterData];

    if (activityType !== "")
      filterResult = filterResult.filter((e) => e.typeEn === activityType);
    if (activityAttribute !== "")
      filterResult = filterResult.filter(
        (e) => e.attributeEn === activityAttribute,
      );
    if (unit !== "") filterResult = filterResult.filter((e) => e.unit === unit);

    return ArrayUtils.convertArrayToMenuData(
      "groupEn",
      "groupEn",
      "groupTh",
      ArrayUtils.filterArrayUniqueByKey("groupEn", filterResult),
    );
  };

  const filterActivityType = () => {
    return ArrayUtils.convertArrayToMenuData(
      "typeEn",
      "typeEn",
      "typeTh",
      ArrayUtils.filterArrayUniqueByKey("typeEn", [
        ...emissionFactorsMasterData,
      ]),
    );
  };

  const filterActivityAttribute = () => {
    const activityType = stepTwoForm.getValues("activityType") ?? "";
    let filterResult: EmissionFactorDataV2[] = [...emissionFactorsMasterData];

    if (activityType !== "") {
      filterResult = filterResult.filter((e) => e.typeEn === activityType);
    }

    return ArrayUtils.convertArrayToMenuData(
      "attributeEn",
      "attributeEn",
      "attributeTh",
      ArrayUtils.filterArrayUniqueByKey("attributeEn", filterResult),
    );
  };

  const filterActivityUnit = () => {
    const activityType = stepTwoForm.getValues("activityType") ?? "";
    const activityAttribute = stepTwoForm.getValues("activityAttribute") ?? "";
    let filterResult: EmissionFactorDataV2[] = [...emissionFactorsMasterData];

    if (activityType !== "")
      filterResult = filterResult.filter((e) => e.typeEn === activityType);
    if (activityAttribute !== "")
      filterResult = filterResult.filter(
        (e) => e.attributeEn === activityAttribute,
      );

    return ArrayUtils.convertArrayToMenuData(
      "unit",
      "unit",
      "unit",
      ArrayUtils.filterArrayUniqueByKey("unit", filterResult),
    );
  };

  const findSelectedFactorGroup = (): EmissionFactorDataV2 | undefined => {
    const activityType = stepTwoForm.getValues("activityType") ?? "";
    const activityAttribute = stepTwoForm.getValues("activityAttribute") ?? "";
    const unit = stepTwoForm.getValues("unit") ?? "";

    if (activityType === "" || activityAttribute === "" || unit === "") {
      return undefined;
    }

    return emissionFactorsMasterData.find(
      (e) =>
        e.attributeEn === activityAttribute &&
        e.typeEn === activityType &&
        e.unit === unit,
    );
  };

  const filterPublishers = () => {
    const emissionFactorsGroup = findSelectedFactorGroup();

    if (!emissionFactorsGroup || !emissionFactorsGroup.publishers) {
      return [];
    }

    return emissionFactorsGroup.publishers.map((p) => {
      const labelName = p.publisherRef?.publishReference || t("Default");
      return {
        value: p.emissionFactorRefId,
        nameEN: labelName,
        nameTH: labelName,
      };
    });
  };

  useEffect(() => {
    clearedFields.current.clear();
  }, [emissionFactorsMasterData]);

  useEffect(() => {
    if (mode === null) {
      buildActivityGroupLabel();
    }
    buildSelect();
  }, [
    emissionFactorsMasterData,
    activityGroup,
    activityType,
    activityAttribute,
    unit,
  ]);

  const selectedFactorGroup = findSelectedFactorGroup();
  const upstreamByFactorId = new Map(
    (selectedFactorGroup?.publishers ?? []).map((p) => [
      p.emissionFactorRefId,
      cat3UpstreamPublisher(selectedFactorGroup?.emissionGroup, p),
    ]),
  );

  const renderUpstreamHint = (
    factorId: string | null | undefined,
    testId: string,
    mt: string,
  ) => {
    const publisher = factorId ? upstreamByFactorId.get(factorId) : undefined;
    if (!publisher) return null;
    return (
      <Typography
        component="span"
        display="block"
        variant="body2"
        color="var(--gray9)"
        lineHeight="16px"
        mt={mt}
        data-testid={testId}
      >
        {t("Activity-Emission-Factor-Upstream-Hint", { publisher })}
      </Typography>
    );
  };

  return (
    <>
      {emissionMode === EmissionMode.AddEmission ? (
        <>
          {/* AddEmission Mode */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Controller
                name="startDate"
                control={control}
                defaultValue={dayjs()}
                rules={{
                  required: true,
                  onChange: (event) => {
                    setValue("endDate", event.target.value);
                    clearErrors("endDate");

                    // ✅ เรียกดึงข้อมูลใหม่เมื่อเปลี่ยนวันที่
                    refreshEmissionFactors();
                  },
                }}
                render={({ field: { onChange, value, ref } }) => (
                  <CustomDatePicker
                    value={value}
                    label={t("Start-Date")}
                    info={t("Start-Date")}
                    required
                    handleOnChange={onChange}
                    defaultDate={value}
                    inputRef={ref}
                    disableFuture
                  />
                )}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Controller
                name="endDate"
                control={control}
                defaultValue={dayjs()}
                rules={{
                  validate: (value) => {
                    const start = dayjs(startDate);
                    const end = dayjs(value);

                    return (
                      value == null ||
                      !end.isAfter(start, "month") ||
                      end.diff(start, "month") <= 11 ||
                      t("end-date-must-be-after-start-date")
                    );
                  },
                  onChange: () => {
                    refreshEmissionFactors();
                  },
                }}
                render={({
                  field: { onChange, value, ref },
                  fieldState: { error },
                }) => (
                  <CustomDatePicker
                    value={value}
                    label={t("End-Date")}
                    info={t("End-Date")}
                    handleOnChange={onChange}
                    defaultDate={null}
                    inputRef={ref}
                    error={error}
                    initDate={getValues("startDate") ?? null}
                    minDate={startDate}
                    maxDate={
                      startDate &&
                      startDate.add(11, "month").isBefore(dayjs(), "month")
                        ? startDate.add(11, "month")
                        : dayjs()
                    }
                    disableFuture
                  />
                )}
              />
            </FormControl>
          </Grid>
        </>
      ) : (
        <>
          {/* AddActivityTemplates */}
          <Grid item xs={6} md={6}>
            <FormControl fullWidth>
              <Controller
                name="year"
                control={control}
                defaultValue={dayjs()}
                rules={{
                  required: true,
                }}
                render={({ field: { onChange, value, ref } }) => (
                  <CustomDatePicker
                    value={value}
                    label={t("Year")}
                    info={t("Year")}
                    required
                    // ✅ แทรก Logic เข้าไปดักจับตอนเปลี่ยนค่า
                    handleOnChange={(newVal) => {
                      onChange(newVal); // 1. อัปเดตค่าให้ช่อง year ปกติ

                      if (newVal) {
                        // 2. แอบไปเซ็ต startDate ให้เป็น 1 มกราคม ของปีนั้น
                        setValue("startDate", dayjs(newVal).startOf("year"));
                        // 3. แอบไปเซ็ต endDate ให้เป็น 31 ธันวาคม ของปีนั้น
                        setValue("endDate", dayjs(newVal).endOf("year"));
                      }

                      // 4. สั่งยิง API (ซึ่งฟังก์ชันนี้จะดึง startDate/endDate ที่เราเพิ่งเซ็ตไปใช้งาน)
                      refreshEmissionFactors();
                    }}
                    defaultDate={value}
                    inputRef={ref}
                    format="YYYY"
                    views={["year"]}
                    maxDate={dayjs().add(1, "year")}
                  />
                )}
              />
            </FormControl>
          </Grid>

          <Grid item xs={6} md={6} maxHeight={"69.13px"}>
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"end"}
              justifyContent={"start"}
              height={"100%"}
            >
              <Controller
                name="monthly"
                control={control}
                defaultValue={true}
                // defaultValue={getValues("monthly") === true ? true : false}
                render={({ field: { onChange, value } }) => (
                  <FormControlLabel
                    control={<Checkbox checked={value} name="monthly" disabled={disableMonthly} />}
                    value={value}
                    onChange={onChange}
                    label={
                      <Typography variant="text1">{t("Monthly")}</Typography>
                    }
                    defaultChecked
                  />
                )}
              />
            </Box>
          </Grid>
        </>
      )}

      {/* 1. Asset Name */}
      <Grid item xs={12} md={6}>
        <CustomInput
          label={t("Asset-Name")}
          info={t("Asset-Name-Description")}
          value={
            assetList.find((i) => i._id === stepOneForm.getValues("assetName"))
              ?.name ?? ""
          }
          disabled
          required
        />
      </Grid>

      <Hidden mdDown>
        <Grid item xs={0} md={6}></Grid>
      </Hidden>

      {/* 2. Activity Type */}
      <Grid item xs={12} md={6}>
        <CustomAutoComplete
          required
          name="activityType"
          defaultValue={getValues("activityType") ?? null}
          rules={{
            required: t("This-field-is-required", {
              param: t("Activity-Type"),
            }),
            onChange: (event) => changeValue(event, "activityType"),
          }}
          control={stepTwoForm.control}
          label={t("Activity-Type")}
          info={t("Activity-Type")}
          placeholder={t("Select-Param", { param: t("Activity-Type") })}
          listMenuItem={filterActivityType()}
        />
      </Grid>

      <Hidden mdDown>
        <Grid item xs={0} md={6}></Grid>
      </Hidden>

      {/* 3. Activity Attribute */}
      <Grid item xs={12} md={6}>
        <CustomAutoComplete
          required
          name="activityAttribute"
          defaultValue={getValues("activityAttribute") ?? null}
          rules={{
            required: t("This-field-is-required", {
              param: t("Activity-Attribute"),
            }),
            onChange: (event) => changeValue(event, "activityAttribute"),
          }}
          control={stepTwoForm.control}
          label={activityAttributeHl}
          info={activityAttributeHl}
          placeholder={t("Select-Param", { param: activityAttributeHl })}
          listMenuItem={filterActivityAttribute()}
          disabled={getValues("activityType") === null ? true : false}
        />
      </Grid>

      {/* 4. Publisher */}
      <Grid item xs={12} md={6}>
        <CustomAutoComplete
          required
          name="activityEmissionFactorPublisher"
          defaultValue={getValues("activityEmissionFactorPublisher") ?? null}
          rules={{
            required: t("This-field-is-required", {
              param: t("Activity-Emission-Factor-Publisher"),
            }),
            onChange: (event) =>
              changeValue(event, "activityEmissionFactorPublisher"),
          }}
          control={stepTwoForm.control}
          label={t("Activity-Emission-Factor-Publisher")}
          info={t("Activity-Emission-Factor-Publisher")}
          placeholder={t("Select-Param", {
            param: t("Activity-Emission-Factor-Publisher"),
          })}
          listMenuItem={filterPublishers()}
          disabled={getValues("unit") === null ? true : false}
          renderOptionLabel={(option) => (
            // the li is a flex row, so the hint needs its own column to sit under the name
            <Box component="span" display="flex" flexDirection="column">
              {option.nameEN}
              {renderUpstreamHint(
                option.value,
                "ef-upstream-hint-option",
                "2px",
              )}
            </Box>
          )}
        />
        {renderUpstreamHint(
          selectedPublisher,
          "ef-upstream-hint-selected",
          "6px",
        )}
      </Grid>

      {/* 5. Vendor */}
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <Controller
            name="vendor"
            control={stepTwoForm.control}
            defaultValue={stepTwoForm.getValues("vendor") ?? ""}
            render={({ field: { onChange, value } }) => (
              <CustomInput
                label={t("Vendor")}
                info={t("Vendor")}
                placeholder={t("Select-Param", { param: t("Vendor") })}
                value={value}
                handleOnChange={onChange}
              />
            )}
          />
        </FormControl>
      </Grid>

      {/* 6. InputAmountTypeRadio */}
      <InputAmountTypeRadio
        activityGroup={emissionGroup ?? undefined}
        activityAttribute={activityAttribute ?? undefined}
      />

      {/* 7. Unit */}
      <Grid item xs={12} md={6}>
        <CustomAutoComplete
          required
          name="unit"
          defaultValue={getValues("unit") ?? null}
          rules={{
            required: t("This-field-is-required", { param: t("Unit") }),
            onChange: (event) => changeValue(event, "unit"),
          }}
          control={stepTwoForm.control}
          label={t("Unit")}
          info={t("Unit")}
          placeholder={t("Select-Param", { param: t("Unit") })}
          listMenuItem={filterActivityUnit()}
          disabled={getValues("activityAttribute") === null ? true : false}
        />
      </Grid>
    </>
  );
};

export default AddEmissionSectionOne;
